/* Palpa Proyectos: orden, filtros por industria/país y paginación en cliente.
 *
 * Shopify limita una sección a 50 bloques, y la lista de proyectos es más larga.
 * Por eso varias secciones se pueden encadenar: la primera es la "líder" (trae
 * encabezado, filtros y paginador) y las siguientes se marcan como continuación.
 * Las tarjetas de las continuaciones se CLONAN dentro de la grilla de la líder
 * —no se mueven— para que al recargarse una sección en el editor de temas la
 * otra no se quede sin sus tarjetas. */

(() => {
  const PER_PAGE_FALLBACK = 6;
  const savedState = new Map();

  const readCard = (el) => ({
    el,
    blockId: el.dataset.ppBlock,
    industry: el.dataset.ppIndustry || '',
    countries: (el.dataset.ppCountries || '').split('|').filter(Boolean),
    sortKey: el.dataset.ppSortKey || '0',
  });

  const byLocale = (a, b) => a.localeCompare(b, 'es');

  function group(roots) {
    const groups = [];
    roots.forEach((root) => {
      const isFollower = root.dataset.ppContinuation === 'true' && groups.length > 0;
      if (isFollower) groups[groups.length - 1].followers.push(root);
      else groups.push({ leader: root, followers: [] });
    });
    return groups;
  }

  function collect({ leader, followers }) {
    const grid = leader.querySelector('[data-pp-grid]');
    grid.querySelectorAll('[data-pp-clone]').forEach((el) => el.remove());

    followers.forEach((root) => {
      root.querySelectorAll('[data-pp-card]').forEach((card) => {
        const clone = card.cloneNode(true);
        clone.dataset.ppClone = 'true';
        grid.appendChild(clone);
      });
      root.hidden = true;
    });

    return Array.from(grid.querySelectorAll('[data-pp-card]')).map(readCard);
  }

  function sort(cards, order) {
    if (order === 'manual') return cards;
    const dir = order === 'date_asc' ? 1 : -1;
    return cards
      .map((card, i) => ({ card, i }))
      .sort((a, b) => {
        const cmp = a.card.sortKey.localeCompare(b.card.sortKey);
        // Empate de fecha: se respeta el orden en que están las tarjetas.
        return cmp === 0 ? a.i - b.i : cmp * dir;
      })
      .map(({ card }) => card);
  }

  /* Las industrias salen en el orden de la lista de la sección; las que no
   * estén en ella van al final, alfabéticas, para que una industria nueva
   * aparezca en el filtro sin tener que editar la lista. */
  function ordered(values, order) {
    const rank = new Map(order.map((name, i) => [name.toLowerCase(), i]));
    const at = (v) => (rank.has(v.toLowerCase()) ? rank.get(v.toLowerCase()) : Infinity);
    return [...values].sort((a, b) => (at(a) === at(b) ? byLocale(a, b) : at(a) - at(b)));
  }

  function fillSelect(el, allLabel, values, current) {
    el.textContent = '';
    [['', allLabel], ...values.map((v) => [v, v])].forEach(([value, label]) => {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = label;
      el.appendChild(opt);
    });
    el.value = current;
  }

  function buildPager(pager, page, pages, onGo) {
    pager.textContent = '';
    if (pages <= 1) {
      pager.hidden = true;
      return;
    }
    pager.hidden = false;

    const step = (label, name, target, disabled) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'palpa-projects__step';
      btn.textContent = label;
      btn.setAttribute('aria-label', name);
      btn.disabled = disabled;
      if (!disabled) btn.addEventListener('click', () => onGo(target));
      return btn;
    };

    pager.appendChild(step('←', 'Página anterior', page - 1, page === 1));

    // Ventana de páginas: primera, última, y las vecinas de la actual.
    const shown = new Set([1, pages, page, page - 1, page + 1]);
    let previous = 0;
    for (let n = 1; n <= pages; n += 1) {
      if (!shown.has(n)) continue;
      if (n - previous > 1) {
        const gap = document.createElement('span');
        gap.className = 'palpa-projects__ellipsis';
        gap.textContent = '…';
        pager.appendChild(gap);
      }
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'palpa-projects__page';
      btn.textContent = String(n);
      if (n === page) btn.setAttribute('aria-current', 'page');
      else btn.addEventListener('click', () => onGo(n));
      pager.appendChild(btn);
      previous = n;
    }

    pager.appendChild(step('→', 'Página siguiente', page + 1, page === pages));
  }

  function start(g) {
    const { leader } = g;
    const grid = leader.querySelector('[data-pp-grid]');
    const filters = leader.querySelector('[data-pp-filters]');
    const pager = leader.querySelector('[data-pp-pager]');
    const empty = leader.querySelector('[data-pp-empty]');
    const countEl = leader.querySelector('[data-pp-count]');
    const resetBtn = leader.querySelector('[data-pp-reset]');
    const perPage = Number(leader.dataset.ppPerPage) || PER_PAGE_FALLBACK;
    const allLabel = leader.dataset.ppAllLabel || 'Todos';

    const cards = sort(collect(g), leader.dataset.ppSort);
    cards.forEach(({ el }) => grid.appendChild(el));

    const key = leader.closest('.shopify-section')?.id || 'palpa-projects';
    const state = savedState.get(key) || { industry: '', country: '', page: 1 };
    savedState.set(key, state);

    const industryOrder = (leader.dataset.ppIndustryOrder || '').split('|').filter(Boolean);
    const industries = ordered(
      new Set(cards.map((c) => c.industry).filter(Boolean)),
      industryOrder
    );
    const countries = [...new Set(cards.flatMap((c) => c.countries))].sort(byLocale);

    if (filters) {
      const options = { industry: industries, country: countries };
      Object.entries(options).forEach(([field, values]) => {
        const holder = filters.querySelector(`[data-pp-filter-group="${field}"]`);
        if (!holder) return;
        // Un filtro con una sola opción no filtra nada: se oculta el grupo.
        holder.hidden = values.length < 2;
        if (holder.hidden) {
          state[field] = '';
          return;
        }
        // Una opción que ya no existe (se borró el último proyecto que la usaba)
        // dejaría el select sin valor y la grilla vacía sin forma de volver.
        if (state[field] && !values.includes(state[field])) state[field] = '';
        fillSelect(holder.querySelector('[data-pp-select]'), allLabel, values, state[field]);
      });
      filters.hidden = industries.length < 2 && countries.length < 2;
    }

    function matching() {
      return cards.filter(
        (c) =>
          (!state.industry || c.industry === state.industry) &&
          (!state.country || c.countries.includes(state.country))
      );
    }

    function render(scroll) {
      const matched = matching();
      const pages = Math.max(1, Math.ceil(matched.length / perPage));
      state.page = Math.min(Math.max(1, state.page), pages);

      const from = (state.page - 1) * perPage;
      const visible = new Set(matched.slice(from, from + perPage).map((c) => c.el));
      cards.forEach(({ el }) => {
        el.hidden = !visible.has(el);
      });

      if (empty) empty.hidden = matched.length > 0;
      if (countEl) {
        countEl.textContent = `${matched.length} ${matched.length === 1 ? 'proyecto' : 'proyectos'}`;
      }
      if (resetBtn) resetBtn.hidden = !state.industry && !state.country;

      if (filters) {
        filters.querySelectorAll('[data-pp-filter-group]').forEach((holder) => {
          const sel = holder.querySelector('[data-pp-select]');
          if (!sel) return;
          const value = state[holder.dataset.ppFilterGroup];
          sel.value = value;
          // Marca el filtro activo; lo lee el CSS para pintarlo en rosa.
          sel.dataset.ppActive = String(Boolean(value));
        });
      }

      if (pager) {
        buildPager(pager, state.page, pages, (n) => {
          state.page = n;
          render(true);
        });
      }

      if (scroll) {
        leader.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    // El API se reemplaza en cada init; los listeners de abajo se registran una
    // sola vez y siempre leen la versión vigente, así una recarga de sección en
    // el editor no deja handlers apuntando a tarjetas que ya no existen.
    leader.ppApi = {
      pick(field, value) {
        state[field] = value;
        state.page = 1;
        render(true);
      },
      reset() {
        state.industry = '';
        state.country = '';
        state.page = 1;
        render(true);
      },
      // En el editor de temas: al seleccionar una tarjeta, salta a su página.
      focusBlock(blockId) {
        const card = cards.find((c) => c.blockId === blockId);
        if (!card) return false;
        if (!matching().includes(card)) {
          state.industry = '';
          state.country = '';
        }
        state.page = Math.floor(matching().indexOf(card) / perPage) + 1;
        render(false);
        card.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return true;
      },
    };

    if (!leader.dataset.ppBound) {
      leader.dataset.ppBound = '1';
      leader.addEventListener('change', (event) => {
        const sel = event.target.closest('[data-pp-select]');
        if (!sel) return;
        leader.ppApi.pick(sel.closest('[data-pp-filter-group]').dataset.ppFilterGroup, sel.value);
      });
      leader.addEventListener('click', (event) => {
        if (event.target.closest('[data-pp-reset]')) leader.ppApi.reset();
      });
    }

    render(false);
  }

  let leaders = [];

  function init() {
    leaders = group(Array.from(document.querySelectorAll('[data-pp-root]'))).map((g) => {
      start(g);
      return g.leader;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  if (window.Shopify && window.Shopify.designMode) {
    const reinit = () => {
      window.requestAnimationFrame(init);
    };
    document.addEventListener('shopify:section:load', reinit);
    document.addEventListener('shopify:section:unload', reinit);
    document.addEventListener('shopify:block:select', (event) => {
      leaders.some((leader) => leader.ppApi && leader.ppApi.focusBlock(event.detail.blockId));
    });
  }
})();
